import { useState, useEffect } from 'react';
import { DrawerForm } from '../../components/ui/drawer-form';
import AddSupportTicketForm from '../../components/support/AddSupportTicketForm';
import { supportTicketAPI } from '../../services/api';
import {
  SupportTicket,
  FAQCategory,
  SupportTicketData,
  FilterOption,
} from '../../utils/types';
import PageHeader from '../../components/common/PageHeader';
import TicketList from '../../components/support/TicketList';
import FAQSection from '../../components/support/FAQSection';
import toast from 'react-hot-toast';
import { MessageCircle, Mail, User, PlusCircle } from 'lucide-react';
import {
  SUPPORT_CATEGORIES,
  SUPPORT_PRIORITIES,
} from '../../constants/supportOptions';
import { Button } from '../../components/ui/button';

type TabType = 'tickets' | 'faq';

const Support = () => {
  const [activeTab, setActiveTab] = useState<TabType>('tickets');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [showOpenTicketDrawer, setShowOpenTicketDrawer] = useState(false);
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [pagination] = useState({
    page: 1,
    limit: 10,
  });

  const FAQ_CATEGORIES: FAQCategory[] = [
    {
      title: 'Account & Security',
      icon: User,
      questions: [
        'How do I reset my password?',
        'How to enable two-factor authentication?',
        'How to update my profile information?',
        'What are the account verification requirements?',
      ],
    },
    {
      title: 'Trading & Platform',
      icon: MessageCircle,
      questions: [
        'What are the trading hours?',
        'How to place a trade?',
        'What are the spreads and commissions?',
        'How to use the trading platform?',
      ],
    },
    {
      title: 'Deposits & Withdrawals',
      icon: Mail,
      questions: [
        'What deposit methods are available?',
        'How long do withdrawals take?',
        'What are the minimum and maximum limits?',
        'Are there any fees for deposits or withdrawals?',
      ],
    },
  ];

  useEffect(() => {
    const fetchUserTickets = async () => {
      try {
        const params = new URLSearchParams();
        params.append('page', pagination.page.toString());
        params.append('limit', pagination.limit.toString());

        if (selectedCategory !== 'all') {
          params.append('category', selectedCategory);
        }

        if (selectedPriority !== 'all') {
          params.append('priority', selectedPriority);
        }

        if (searchTerm) {
          params.append('search', searchTerm);
        }

        const response = await supportTicketAPI.getUserTickets(
          params.toString()
        );

        if (response.success) {
          setTickets(response.data?.tickets || []);
        } else {
          console.error('Failed to fetch tickets:', response.error);
          toast.error(response.error || 'Failed to fetch tickets');
        }
      } catch (error) {
        console.error('Error fetching tickets:', error);
        toast.error('Error fetching support tickets');
      }
    };

    fetchUserTickets();
  }, [
    pagination.page,
    pagination.limit,
    selectedCategory,
    selectedPriority,
    searchTerm,
  ]);

  const handleCreateSupportTicket = () => {
    setShowOpenTicketDrawer(true);
  };

  const handleSupportTicketSubmit = async (data: SupportTicketData) => {
    try {
      setIsSubmittingTicket(true);
      const ticketData = {
        subject: data.subject,
        category: data.category,
        priority: data.priority,
        emailAddress: data.email,
        accountNumber: data.accountNumber,
        issueDescription: data.issueDescription,
        attachments: data.attachments,
      };

      const response = await supportTicketAPI.createTicket(ticketData);

      if (response.success) {
        toast.success(
          response.message || 'Support ticket created successfully'
        );
        setShowOpenTicketDrawer(false);
        const refreshResponse = await supportTicketAPI.getUserTickets();
        if (refreshResponse.success) {
          setTickets(refreshResponse.data?.tickets || []);
        }
      } else {
        console.error('Failed to create support ticket:', response.error);
        toast.error(response.error || 'Failed to create support ticket');
      }
    } catch (error) {
      console.error('Error creating support ticket:', error);
      toast.error('Error creating support ticket');
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  const handleEditTicket = async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page.toString());
      params.append('limit', pagination.limit.toString());

      if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }

      if (selectedPriority !== 'all') {
        params.append('priority', selectedPriority);
      }

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await supportTicketAPI.getUserTickets(params.toString());

      if (response.success) {
        setTickets(response.data?.tickets || []);
      } else {
        toast.error('Failed to refresh ticket data');
      }
    } catch (error) {
      toast.error('Error refreshing ticket data');
    }
  };

  const handleDeleteTicket = async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page.toString());
      params.append('limit', pagination.limit.toString());

      if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }

      if (selectedPriority !== 'all') {
        params.append('priority', selectedPriority);
      }

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await supportTicketAPI.getUserTickets(params.toString());

      if (response.success) {
        setTickets(response.data?.tickets || []);
      } else {
        toast.error('Failed to refresh ticket data after deletion');
      }
    } catch (error) {
      toast.error('Error refreshing ticket data after deletion');
    }
  };

  const getTabButtonClass = (tab: 'tickets' | 'faq') => {
    return `text-lg font-medium ${
      activeTab === tab ? 'text-primary' : 'text-black'
    }`;
  };

  return (
    <div className="relative">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Support Center"
          description="Get help with your account, trading, and platform issues"
          actionLabel="Add Support Ticket"
          actionIcon={PlusCircle}
          onAction={handleCreateSupportTicket}
        />

        <div className="border-b border-border">
          <nav className="flex space-x-8">
            <Button
              onClick={() => setActiveTab('tickets')}
              className={getTabButtonClass('tickets')}
              variant="ghost"
            >
              My Tickets
            </Button>
            <Button
              onClick={() => setActiveTab('faq')}
              className={getTabButtonClass('faq')}
              variant="ghost"
            >
              FAQ
            </Button>
          </nav>
        </div>
      </div>

      <div className="pt-4">
        {activeTab === 'tickets' && (
          <TicketList
            tickets={tickets}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            selectedPriority={selectedPriority}
            onPriorityChange={setSelectedPriority}
            categories={SUPPORT_CATEGORIES as unknown as FilterOption[]}
            priorities={SUPPORT_PRIORITIES as unknown as FilterOption[]}
            onEditTicket={handleEditTicket}
            onDeleteTicket={handleDeleteTicket}
          />
        )}

        {activeTab === 'faq' && <FAQSection faqCategories={FAQ_CATEGORIES} />}
      </div>

      <DrawerForm
        open={showOpenTicketDrawer}
        onOpenChange={() => setShowOpenTicketDrawer(false)}
        title="Create Support Ticket"
        description="Submit a new support ticket and we'll get back to you as soon as possible."
        loading={isSubmittingTicket}
      >
        <AddSupportTicketForm onSubmit={handleSupportTicketSubmit} />
      </DrawerForm>
    </div>
  );
};

export default Support;
